import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "sensitivity-body-container" }
const _hoisted_2 = {
  class: "preset-tabs",
  role: "tablist",
  "aria-label": "灵敏度档位"
}
const _hoisted_3 = ["aria-selected", "tabindex", "disabled", "onClick"]
const _hoisted_4 = { class: "sensitivity-canvas-container" }
const _hoisted_5 = { class: "title" }
const _hoisted_6 = ["id"]
const _hoisted_7 = { class: "title" }
const _hoisted_8 = { class: "slider-container" }
const _hoisted_9 = { class: "slider-content" }
const _hoisted_10 = { class: "input-container" }

import { computed, getCurrentInstance, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { getUserSensibility, setUserSensibility } from '@/service'
import { $enum } from '@/global'
import ExpCurveGraph from './ExpCurveGraph'

type PresetType = 'LOW' | 'MEDIUM' | 'HIGH' | 'CUSTOM'
type FixedPresetType = Exclude<PresetType, 'CUSTOM'>
type AxisKey = 'throttle' | 'yaw' | 'pitch' | 'roll'

interface SensitivityValue {
    keyTypeId: string | number
    keyCode: string
    sensitivityValue: number
    customSensitivityValue?: number
}

interface PresetPreview {
    presetType: FixedPresetType
    sensitivityList: SensitivityValue[]
}

interface SensitivityResponse {
    presetType: PresetType
    sensitivityList: SensitivityValue[]
    presetList: PresetPreview[]
}

interface AxisState {
    id: string
    ExpValue: number
    upTitle: string
    downTitle: string
    min: number
    max: number
    step: number
    idName: string
    drawer: ExpCurveGraph | null
    keyName: AxisKey
}

interface SaveSensitivityRequest {
    userId: string
    presetType: PresetType
    keyTypeSensitivityMap?: Record<string, number>
}

const messageDuration = 1200

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const store = useStore()
const proxy = getCurrentInstance()?.proxy
const loading = ref(false)
const restoreLoading = ref(false)
const saveLoading = ref(false)
const activePreset = ref<PresetType>('MEDIUM')

const presetOptions: Array<{ label: string; value: PresetType }> = [
    { label: '低', value: 'LOW' },
    { label: '中', value: 'MEDIUM' },
    { label: '高', value: 'HIGH' },
    { label: '自定义', value: 'CUSTOM' },
]

const axisKeys: AxisKey[] = ['throttle', 'yaw', 'pitch', 'roll']
const fixedPresetTypes: FixedPresetType[] = ['LOW', 'MEDIUM', 'HIGH']

const state = reactive<Record<AxisKey, AxisState>>({
    throttle: {
        id: '',
        ExpValue: 0.5,
        upTitle: '上升',
        downTitle: '下降',
        min: 0.1,
        max: 0.9,
        step: 0.01,
        idName: 'up-down',
        drawer: null,
        keyName: 'throttle',
    },
    yaw: {
        id: '',
        ExpValue: 0.4,
        upTitle: '左转',
        downTitle: '右转',
        min: 0.1,
        max: 0.9,
        step: 0.01,
        idName: 'turn',
        drawer: null,
        keyName: 'yaw',
    },
    pitch: {
        id: '',
        ExpValue: 0.4,
        upTitle: '前进',
        downTitle: '后退',
        min: 0.1,
        max: 0.9,
        step: 0.01,
        idName: 'forward-backward',
        drawer: null,
        keyName: 'pitch',
    },
    roll: {
        id: '',
        ExpValue: 0.5,
        upTitle: '向右',
        downTitle: '向左',
        min: 0.1,
        max: 0.9,
        step: 0.01,
        idName: 'right-left',
        drawer: null,
        keyName: 'roll',
    },
})

const fixedPresetValues = reactive<Record<FixedPresetType, Partial<Record<AxisKey, number>>>>({
    LOW: {},
    MEDIUM: {},
    HIGH: {},
})
const customSensitivityValues = reactive<Partial<Record<AxisKey, number>>>({})
const isCustomPreset = computed(() => activePreset.value === 'CUSTOM')

axisKeys.forEach(key => {
    watch(
        () => state[key].ExpValue,
        value => {
            if (Number.isFinite(value)) {
                state[key].drawer?.setExpValue(value)
                if (isCustomPreset.value) {
                    customSensitivityValues[key] = value
                }
            }
        },
    )
})

function isAxisKey(value: string): value is AxisKey {
    return axisKeys.includes(value as AxisKey)
}

function isPresetType(value: string): value is PresetType {
    return presetOptions.some(option => option.value === value)
}

function toSensitivityValue(value: unknown): number | null {
    const result = Number(value)
    return Number.isFinite(result) ? Math.round(result * 100) / 100 : null
}

function parseSensitivityData(data: SensitivityResponse): boolean {
    if (!data || !Array.isArray(data.sensitivityList) || !Array.isArray(data.presetList)) {
        return false
    }

    const currentValues: Partial<Record<AxisKey, number>> = {}
    const customValues: Partial<Record<AxisKey, number>> = {}
    const axisIds: Partial<Record<AxisKey, string>> = {}

    data.sensitivityList.forEach(item => {
        if (!isAxisKey(item.keyCode)) return
        const currentValue = toSensitivityValue(item.sensitivityValue)
        const customValue = toSensitivityValue(item.customSensitivityValue)
        if (currentValue !== null) currentValues[item.keyCode] = currentValue
        if (customValue !== null) customValues[item.keyCode] = customValue
        axisIds[item.keyCode] = String(item.keyTypeId)
    })

    const parsedPresetValues: Record<FixedPresetType, Partial<Record<AxisKey, number>>> = {
        LOW: {},
        MEDIUM: {},
        HIGH: {},
    }

    data.presetList.forEach(preset => {
        if (!fixedPresetTypes.includes(preset.presetType) || !Array.isArray(preset.sensitivityList)) return
        preset.sensitivityList.forEach(item => {
            if (!isAxisKey(item.keyCode)) return
            const value = toSensitivityValue(item.sensitivityValue)
            if (value !== null) parsedPresetValues[preset.presetType][item.keyCode] = value
        })
    })

    for (const key of axisKeys) {
        const axisId = axisIds[key]
        const currentValue = currentValues[key]
        const customValue = customValues[key]
        if (!axisId || currentValue === undefined || customValue === undefined) return false
    }
    for (const preset of fixedPresetTypes) {
        for (const key of axisKeys) {
            const value = parsedPresetValues[preset][key]
            if (value === undefined) return false
        }
    }

    for (const key of axisKeys) {
        state[key].id = axisIds[key] as string
        state[key].ExpValue = currentValues[key] as number
        customSensitivityValues[key] = customValues[key] as number
    }
    for (const preset of fixedPresetTypes) {
        for (const key of axisKeys) {
            const value = parsedPresetValues[preset][key] as number
            fixedPresetValues[preset][key] = value
        }
    }
    activePreset.value = isPresetType(data.presetType) ? data.presetType : 'MEDIUM'
    return true
}

async function loadSensitivity(showError = true): Promise<boolean> {
    const userId = String(store.state.persistence.userInfo.userId || '')
    if (!userId) return false

    loading.value = true
    try {
        const { code, data } = await getUserSensibility({ id: userId })
        if (code !== $enum.EnumResult.SUCCESS || !parseSensitivityData(data)) {
            if (showError) {
                proxy?.vknMessage.error({ message: '灵敏度设置加载失败！', duration: messageDuration })
            }
            return false
        }
        return true
    } catch (error) {
        if (showError) {
            proxy?.vknMessage.error({ message: '灵敏度设置加载失败！', duration: messageDuration })
        }
        return false
    } finally {
        loading.value = false
    }
}

function rememberCustomValues() {
    if (!isCustomPreset.value) return
    axisKeys.forEach(key => {
        if (Number.isFinite(state[key].ExpValue)) {
            customSensitivityValues[key] = state[key].ExpValue
        }
    })
}

function applyPreset(preset: PresetType): boolean {
    rememberCustomValues()
    const values = preset === 'CUSTOM' ? customSensitivityValues : fixedPresetValues[preset]
    const nextValues: number[] = []
    for (const key of axisKeys) {
        const value = values[key]
        if (value === undefined) return false
        nextValues.push(value)
    }

    activePreset.value = preset
    axisKeys.forEach((key, index) => {
        state[key].ExpValue = nextValues[index]
    })
    return true
}

function selectPreset(preset: PresetType) {
    if (preset === activePreset.value || loading.value || restoreLoading.value || saveLoading.value) return
    window?.playClick()
    applyPreset(preset)
}

async function resetSensitivity() {
    if (loading.value || restoreLoading.value || saveLoading.value) return
    window?.playClick()

    restoreLoading.value = true
    try {
        const { code, data } = await getUserSensibility({ id: '-1' })
        if (code !== $enum.EnumResult.SUCCESS || !parseSensitivityData(data)) {
            proxy?.vknMessage.error({ message: '恢复默认失败！', duration: messageDuration })
            return
        }
        proxy?.vknMessage.success({ message: '恢复默认成功！', duration: messageDuration })
    } catch (error) {
        proxy?.vknMessage.error({ message: '恢复默认失败！', duration: messageDuration })
    } finally {
        restoreLoading.value = false
    }
}

function isValidSensitivity(value: number): boolean {
    return Number.isFinite(value)
        && value >= 0.1
        && value <= 0.9
        && Math.abs(value * 100 - Math.round(value * 100)) < 0.000001
}

function handleBlur(item: AxisState) {
    if (!isCustomPreset.value) return
    if (!isValidSensitivity(item.ExpValue)) {
        item.ExpValue = item.min
    }
}

function buildSaveRequest(): SaveSensitivityRequest | null {
    const userId = String(store.state.persistence.userInfo.userId || '')
    if (!userId) return null

    const request: SaveSensitivityRequest = {
        userId,
        presetType: activePreset.value,
    }
    if (!isCustomPreset.value) return request

    const keyTypeSensitivityMap: Record<string, number> = {}
    for (const key of axisKeys) {
        const item = state[key]
        if (!item.id || !isValidSensitivity(item.ExpValue)) return null
        keyTypeSensitivityMap[String(item.id)] = Math.round(item.ExpValue * 100) / 100
    }
    if (Object.keys(keyTypeSensitivityMap).sort().join(',') !== '1,2,3,4') return null
    request.keyTypeSensitivityMap = keyTypeSensitivityMap
    return request
}

async function saveSensitivity() {
    if (loading.value || restoreLoading.value || saveLoading.value) return
    window?.playClick()
    const request = buildSaveRequest()
    if (!request) {
        proxy?.vknMessage.error({ message: '请检查四轴灵敏度设置！', duration: messageDuration })
        return
    }

    saveLoading.value = true
    try {
        const { code } = await setUserSensibility(request)
        if (code !== $enum.EnumResult.SUCCESS) {
            proxy?.vknMessage.error({ message: '保存失败！', duration: messageDuration })
            return
        }
        proxy?.vknMessage.success({ message: '保存成功！', duration: messageDuration })
        await loadSensitivity(false)
    } catch (error) {
        proxy?.vknMessage.error({ message: '保存失败！', duration: messageDuration })
    } finally {
        saveLoading.value = false
    }
}

function initExpCurveGraph(retry = 0) {
    const items = axisKeys.map(key => state[key])
    const containers = items.map(item => document.querySelector(`#${item.idName}`))
    if (containers.some(container => !container)) {
        if (retry < 100) {
            setTimeout(() => initExpCurveGraph(retry + 1), 50)
        }
        return
    }
    state.throttle.drawer = new ExpCurveGraph({ container: containers[0], expValue: state.throttle.ExpValue, curveColor: 0x2174FF })
    state.yaw.drawer = new ExpCurveGraph({ container: containers[1], expValue: state.yaw.ExpValue, curveColor: 0x3CB1FB })
    state.pitch.drawer = new ExpCurveGraph({ container: containers[2], expValue: state.pitch.ExpValue, curveColor: 0xFFA314 })
    state.roll.drawer = new ExpCurveGraph({ container: containers[3], expValue: state.roll.ExpValue, curveColor: 0x46B65B })
}

function handlerResize() {
    axisKeys.forEach(key => state[key].drawer?.resize())
}

onMounted(() => {
    initExpCurveGraph()
    loadSensitivity()
    window.addEventListener('resize', handlerResize)
})

onUnmounted(() => {
    window.removeEventListener('resize', handlerResize)
})

return (_ctx: any,_cache: any) => {
  const _component_el_slider = _resolveComponent("el-slider")!
  const _component_el_input_number = _resolveComponent("el-input-number")!
  const _component_vkm_settingChildPage = _resolveComponent("vkm-settingChildPage")!

  return (_openBlock(), _createBlock(_component_vkm_settingChildPage, {
    class: "sensitivity-container",
    "restore-text": "恢复默认",
    "restore-loading": restoreLoading.value,
    "save-loading": saveLoading.value,
    onRestore: resetSensitivity,
    onSave: saveSensitivity
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(presetOptions, (option) => {
            return _createElementVNode("button", {
              key: option.value,
              class: _normalizeClass(["preset-tab", { active: activePreset.value === option.value }]),
              type: "button",
              role: "tab",
              "aria-selected": activePreset.value === option.value,
              tabindex: activePreset.value === option.value ? 0 : -1,
              disabled: loading.value || restoreLoading.value || saveLoading.value,
              onClick: ($event: any) => (selectPreset(option.value))
            }, _toDisplayString(option.label), 11, _hoisted_3)
          }), 64))
        ]),
        _createElementVNode("div", _hoisted_4, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(axisKeys, (key) => {
            return _createElementVNode("div", {
              key: key,
              class: "item-container"
            }, [
              _createElementVNode("p", _hoisted_5, _toDisplayString(state[key].upTitle), 1),
              _createElementVNode("div", {
                class: "canvas-container",
                id: state[key].idName
              }, null, 8, _hoisted_6),
              _createElementVNode("p", _hoisted_7, _toDisplayString(state[key].downTitle), 1),
              _createElementVNode("div", _hoisted_8, [
                _cache[0] || (_cache[0] = _createElementVNode("span", { class: "slider-label" }, "EXP", -1)),
                _createElementVNode("div", _hoisted_9, [
                  _createVNode(_component_el_slider, {
                    modelValue: state[key].ExpValue,
                    "onUpdate:modelValue": ($event: any) => ((state[key].ExpValue) = $event),
                    class: "custom-controller-slider",
                    step: state[key].step,
                    min: state[key].min,
                    max: state[key].max,
                    "show-tooltip": false,
                    disabled: !isCustomPreset.value || loading.value || restoreLoading.value || saveLoading.value
                  }, null, 8, ["modelValue", "onUpdate:modelValue", "step", "min", "max", "disabled"])
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(_component_el_input_number, {
                    modelValue: state[key].ExpValue,
                    "onUpdate:modelValue": ($event: any) => ((state[key].ExpValue) = $event),
                    class: "custom-numner-input",
                    precision: 2,
                    step: state[key].step,
                    min: state[key].min,
                    max: state[key].max,
                    controls: false,
                    disabled: !isCustomPreset.value || loading.value || restoreLoading.value || saveLoading.value,
                    onBlur: ($event: any) => (handleBlur(state[key]))
                  }, null, 8, ["modelValue", "onUpdate:modelValue", "step", "min", "max", "disabled", "onBlur"])
                ])
              ])
            ])
          }), 64))
        ]),
        _cache[1] || (_cache[1] = _createElementVNode("p", { class: "description" }, "说明：X轴为摇杆物理输出量，Y轴为摇杆逻辑输出量，EXP系数范围0.1-0.9。", -1))
      ])
    ]),
    _: 1
  }, 8, ["restore-loading", "save-loading"]))
}
}

})